import { queryClient } from '@/app/providers/react-query-provider';

import { apiFileFetch, apiInvestmentFetch } from '..';
import { ADMIN_VIEWS_FUNDS_QUERY_KEY } from '../admin/queries';
import { _postEntityUploadRequest } from '../entity/mutations';
import { UploadRequestResponse } from '../files/types';
import { _postUserUploadRequest } from '../user/mutations';
import type { NavDataRes } from './queries';
import { FUND_QUERY_KEYS } from './queries';
import { EditFundMemberBody, FundMemberMutationParams } from './types';

///////////////////////////////////////////////
export type FileMetadata = {
  filename: string;
  contentType: string;
  contentEncoding?: string;
  category: 'logo' | 'profile' | 'document';
  ownerType: 'fund' | 'entity' | 'user';
  ownerId: string | null;
};

export const _postFiles = async (metadata: FileMetadata & { file: File }) => {
  if (metadata.ownerType === 'entity') {
    if (!metadata.ownerId) {
      throw new Error('ownerId is required for entity file uploads');
    }

    return _postEntityUploadRequest({
      entityId: String(metadata.ownerId),
      filename: metadata.filename,
      contentType: metadata.contentType,
      contentEncoding: metadata.contentEncoding,
      category: metadata.category,
    });
  }

  if (metadata.ownerType === 'user') {
    if (!metadata.ownerId) {
      throw new Error('ownerId is required for user file uploads');
    }

    return _postUserUploadRequest({
      userId: String(metadata.ownerId),
      filename: metadata.filename,
      contentType: metadata.contentType,
      contentEncoding: metadata.contentEncoding,
      category: metadata.category,
    });
  }

  return apiFileFetch<UploadRequestResponse>('/files/upload-request', {
    method: 'POST',
    body: {
      filename: metadata.filename,
      contentType: metadata.contentType,
      contentEncoding: metadata.contentEncoding,
      category: metadata.category,
      ownerType: metadata.ownerType,
      ownerId: metadata.ownerId,
    },
  });
};

///////////////////////////////////////////////
export type FundFileUploadRequest = {
  fundId: string;
  filename: string;
  contentType: string;
  contentEncoding?: string;
  category: 'logo' | 'profile' | 'document';
  file: File;
};

export type FundUploadRequestResponse = UploadRequestResponse;

export const _postFundUploadRequest = async ({
  fundId,
  filename,
  contentType,
  contentEncoding,
  category,
}: FundFileUploadRequest) => {
  if (!fundId) {
    throw new Error('fundId is required for fund file uploads');
  }

  return apiInvestmentFetch<FundUploadRequestResponse>(`/funds/${fundId}/upload-request`, {
    method: 'POST',
    body: {
      filename,
      contentType,
      contentEncoding,
      category,
    },
  });
};

export type PatchFileFilenameRequest = {
  fundId: string;
  fileId: string;
  filename: string;
};

export type PatchFileFilenameResponse = {
  id: string;
  fileName: string;
  visibility: 'private' | 'public';
  contentType: string;
  contentEncoding: string | null;
  category: 'logo' | 'profile' | 'document';
  ownerType: 'fund' | 'entity' | 'user';
  ownerId: string;
  size: number;
  url: string | null;
  urlExpiresAt: string | null;
  uploadedAt: string;
  updatedAt: string;
};

export const _patchFileFilename = ({ fundId, fileId, filename }: PatchFileFilenameRequest) => {
  return apiInvestmentFetch<PatchFileFilenameResponse>(
    `/funds/${fundId}/files/${fileId}/filename`,
    {
      method: 'PATCH',
      body: { filename },
    },
  );
};

///////////////////////////////////////////////

//eslint-disable-next-line @typescript-eslint/no-explicit-any
type FundPayload = BodyInit | Record<string, any> | null | undefined;

export const _postFunds = (body: FundPayload) => {
  return apiInvestmentFetch('/funds', {
    method: 'POST',
    body,
    keepalive: true,
    onResponse: () => {
      queryClient.invalidateQueries({ queryKey: [ADMIN_VIEWS_FUNDS_QUERY_KEY.fundsRoot] });
    },
  });
};

type PutFundPayload = {
  id: string;
  body: FundPayload;
};

export const _putFunds = ({ id, body }: PutFundPayload) => {
  return apiInvestmentFetch(`/funds/${id}`, {
    method: 'PUT',
    body,
    keepalive: true,
    onResponse: () => {
      queryClient.invalidateQueries({ queryKey: [ADMIN_VIEWS_FUNDS_QUERY_KEY.fundsRoot] });
    },
  });
};

export const _closeFund = (id: string) => {
  return apiInvestmentFetch(`/funds/${id}/close`, {
    method: 'PUT',
    onResponse: () => {
      queryClient.invalidateQueries({ queryKey: [FUND_QUERY_KEYS.funds, id] });
    },
  });
};

///////////////////////////////////////////////

export const _submitFunds = (id: string) => {
  return apiInvestmentFetch(`/funds/${id}/submit`, {
    method: 'POST',
  });
};

///////////////////////////////////////////////

export const _checkName = (name: string) => {
  return apiInvestmentFetch<{ available: boolean }>(`/funds/check-name/${name}`, {
    method: 'GET',
  });
};

///////////////////////////////////////////////

export const _fundFollow = (body: { fundId: string }) => {
  return apiInvestmentFetch(`/fund-followers/follow`, {
    method: 'POST',
    body,
    onResponse: () => {
      queryClient.invalidateQueries({ queryKey: [FUND_QUERY_KEYS.userFollowedFunds] });
    },
  });
};

///////////////////////////////////////////////

export const _fundUnfollow = (body: { fundId: string }) => {
  return apiInvestmentFetch(`/fund-followers/unfollow`, {
    method: 'POST',
    body,
    onResponse: () => {
      queryClient.invalidateQueries({ queryKey: [FUND_QUERY_KEYS.userFollowedFunds] });
    },
  });
};

///////////////////////////////////////////////

export const _postFundsDdPackRequest = (body: { fundId: string }) => {
  return apiInvestmentFetch(`/funds-dd-pack-requests`, {
    method: 'POST',
    body,
    onResponse: () => {
      queryClient.invalidateQueries({ queryKey: [FUND_QUERY_KEYS.fundsPublicById] });
      queryClient.invalidateQueries({ queryKey: [FUND_QUERY_KEYS.funds, body.fundId] });
    },
  });
};

///////////////////////////////////////////////

export const _setNetAssetsValuesMutation = ({
  id,
  navData,
}: {
  id: string;
  navData: NavDataRes;
}) => {
  return apiInvestmentFetch(`/funds/${id}/net-assets-values/batch`, {
    method: 'POST',
    body: navData,
  });
};

///////////////////////////////////////////////

export type FundInvitationPayload = {
  email: string;
  title: string;
};

export const _createFundInvitation = ({
  id,
  body,
}: {
  id: string | null;
  body: FundInvitationPayload;
}) => {
  return apiInvestmentFetch<{ id: string }>(`/funds/${id}/member-invitations`, {
    method: 'POST',
    body,
  });
};

///////////////////////////////////////////////

export const _deleteFundInvitation = ({
  id,
  invitationId,
}: {
  id: string;
  invitationId: string;
}) => {
  return apiInvestmentFetch(`/funds/${id}/member-invitations/${invitationId}`, {
    method: 'DELETE',
  });
};

///////////////////////////////////////////////

export const _deleteFundMember = ({ id, memberId }: FundMemberMutationParams) => {
  return apiInvestmentFetch(`/funds/${id}/members/${memberId}`, {
    method: 'DELETE',
  });
};

export const _editFundMember = ({
  id,
  memberId,
  body,
}: FundMemberMutationParams & { body: EditFundMemberBody }) => {
  return apiInvestmentFetch(`/funds/${id}/members/${memberId}`, {
    method: 'PUT',
    body,
  });
};

///////////////////////////////////////////////

export const _approveFund = (id: string) => {
  return apiInvestmentFetch(`/funds/${id}/approve`, {
    method: 'POST',
    onResponse: () => {
      queryClient.invalidateQueries({ queryKey: ADMIN_VIEWS_FUNDS_QUERY_KEY.fundsRoot });
      queryClient.invalidateQueries({ queryKey: [FUND_QUERY_KEYS.funds, id] });
    },
  });
};

///////////////////////////////////////////////

export const _deleteFund = (fundId: string) => {
  return apiInvestmentFetch(`/admin/funds/${fundId}`, {
    method: 'DELETE',
  });
};

export type BlockOrRejectFundPayload = {
  fundId: string;
  reason: string;
  internalReference: string;
};

export const _blockOrRejectFund = ({
  fundId,
  reason,
  internalReference,
}: BlockOrRejectFundPayload) => {
  return apiInvestmentFetch(`/admin/funds/${fundId}/block-or-reject`, {
    method: 'POST',
    body: { reason, internalReference },
  });
};

export const _unblockFund = (fundId: string) => {
  return apiInvestmentFetch(`/admin/funds/${fundId}/unblock`, {
    method: 'POST',
  });
};

export const _setFundNavDataVerification = ({
  fundId,
  verified,
}: {
  fundId: string;
  verified: boolean;
}) => {
  return apiInvestmentFetch(`/admin/funds/${fundId}/nav-data-verification`, {
    method: 'PUT',
    body: { verified },
    onResponse: () => {
      queryClient.invalidateQueries({ queryKey: [FUND_QUERY_KEYS.funds, fundId] });
      queryClient.invalidateQueries({ queryKey: [FUND_QUERY_KEYS.fundsPublicById] });
    },
  });
};

export const _setFundDocumensoTemplateId = ({
  fundId,
  documensoTemplateId,
}: {
  fundId: string;
  documensoTemplateId: string | null;
}) => {
  return apiInvestmentFetch(`/admin/funds/${fundId}/documenso-template-id`, {
    method: 'PUT',
    body: { documensoTemplateId },
    onResponse: () => {
      queryClient.invalidateQueries({ queryKey: [FUND_QUERY_KEYS.funds, fundId] });
      queryClient.invalidateQueries({ queryKey: [FUND_QUERY_KEYS.fundsPublicById] });
    },
  });
};

export const _resendFundShareholderInvitation = (params: { email: string }) => {
  return apiInvestmentFetch(`/admin/funds/shareholder-invitations/resend`, {
    method: 'POST',
    body: { email: params.email },
  });
};
